/* Visuais de carro: peças procedurais (estilos) + roda. Tudo em coordenadas locais do chassi.
 * Convenção: frente do kart = +Z. Rodas ficam FORA do corpo (a física move as rodas do jogador local). */
import * as THREE from 'three'

export const WHEEL_OFFSETS = [
    [1.3, -0.2, 1.5], [-1.3, -0.2, 1.5], [1.3, -0.2, -1.3], [-1.3, -0.2, -1.3],
]

const textureLoader = new THREE.TextureLoader()
const texCache = new Map()
function loadSkinTexture(url) {
    if (!texCache.has(url)) {
        const t = textureLoader.load(url)
        t.encoding = THREE.sRGBEncoding
        texCache.set(url, t)
    }
    return texCache.get(url)
}

// Materiais PBR: highlights e resposta de luz mais próximos de carroceria automotiva.
const toon = (color, extra = {}) => new THREE.MeshStandardMaterial({ color, roughness: 0.34, metalness: 0.28, ...extra })
function box(group, w, h, d, x, y, z, mat) {
    const m = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat)
    m.position.set(x, y, z)
    m.castShadow = true
    group.add(m)
    return m
}

export function makeWheel(radius, width, accent) {
    const g = new THREE.Group()
    const tire = new THREE.Mesh(
        new THREE.CylinderGeometry(radius, radius, width, 32),
        toon(0x0a0a0a, { emissive: 0x1a1a1a }))
    tire.castShadow = true
    g.add(tire)
    const rim = new THREE.Mesh(
        new THREE.CylinderGeometry(radius * 0.65, radius * 0.65, width + 0.05, 16),
        toon(0xCCCCCC, { emissive: accent, emissiveIntensity: 0.3 }))
    g.add(rim)
    for (let i = 0; i < 5; i++) {
        const s = new THREE.Mesh(new THREE.BoxGeometry(0.08, radius * 0.4, 0.04), toon(accent))
        s.rotation.z = (i / 5) * Math.PI * 2
        rim.add(s)
    }
    g.add(new THREE.Mesh(new THREE.CylinderGeometry(radius * 0.2, radius * 0.2, width + 0.1, 16), toon(accent)))
    return g
}

/** 4 rodas estáticas (usadas nos karts remotos). */
export function makeStaticWheels(accent) {
    return WHEEL_OFFSETS.map(([x, y, z]) => {
        const w = makeWheel(0.55, 0.45, accent)
        w.rotation.z = Math.PI / 2
        w.position.set(x, y, z)
        return w
    })
}

const dark = () => toon(0x222222)

const STYLES = {
    // kart clássico (o mesmo visual de antes)
    kart(g, body, accent) {
        box(g, 1.9, 0.6, 3.2, 0, 0.25, 0, body)
        box(g, 1.5, 0.45, 0.8, 0, 0.12, 1.9, body)
        box(g, 1.8, 0.22, 0.25, 0, 0.05, 2.2, toon(0x1a1a1a))
        box(g, 1.35, 0.35, 1.5, 0, 0.62, -0.15, dark())
        box(g, 1.1, 0.28, 0.55, 0, 0.18, -1.75, toon(0x333333))
        box(g, 0.15, 0.6, 0.15, -0.6, 0.5, -1.4, toon(0x333333))
        box(g, 0.15, 0.6, 0.15, 0.6, 0.5, -1.4, toon(0x333333))
        box(g, 1.6, 0.1, 0.45, 0, 0.85, -1.95, accent)
        const hl = toon(0xFFFACD, { emissive: 0xFFD700, emissiveIntensity: 0.6 })
        for (const x of [-0.5, 0.5]) {
            const l = new THREE.Mesh(new THREE.CircleGeometry(0.15, 16), hl)
            l.position.set(x, 0.15, 2.25)
            g.add(l)
        }
    },
    // fórmula 1: baixo, comprido, asas grandes
    formula(g, body, accent) {
        box(g, 1.4, 0.4, 3.6, 0, 0.2, 0, body)
        box(g, 0.9, 0.3, 1.2, 0, 0.1, 2.3, body)
        box(g, 2.3, 0.08, 0.55, 0, -0.05, 2.9, accent)
        box(g, 0.8, 0.35, 1.0, 0, 0.55, -0.2, dark())
        box(g, 0.5, 0.5, 0.6, 0, 0.8, -1.0, toon(0x333333))
        box(g, 0.15, 0.7, 0.15, -0.6, 0.55, -1.8, toon(0x333333))
        box(g, 0.15, 0.7, 0.15, 0.6, 0.55, -1.8, toon(0x333333))
        box(g, 1.9, 0.1, 0.5, 0, 1.0, -1.9, accent)
    },
    // picape: cabine alta + caçamba
    truck(g, body, accent) {
        box(g, 1.9, 0.5, 3.4, 0, 0.2, 0, body)
        box(g, 1.7, 0.9, 1.4, 0, 0.9, -0.2, body)
        box(g, 1.5, 0.45, 0.9, 0, 0.95, -0.2, toon(0x9fd3ff))
        box(g, 1.7, 0.35, 1.2, 0, 0.5, 1.2, body)
        box(g, 1.8, 0.4, 1.1, 0, 0.55, -1.4, toon(0x333333))
        box(g, 2.0, 0.22, 0.25, 0, 0.0, 1.85, accent)
        const hl = toon(0xFFFACD, { emissive: 0xFFD700, emissiveIntensity: 0.6 })
        for (const x of [-0.6, 0.6]) {
            const l = new THREE.Mesh(new THREE.CircleGeometry(0.18, 16), hl)
            l.position.set(x, 0.45, 1.82)
            g.add(l)
        }
    },
    // conversível minúsculo (Vital — urso enorme quase não cabe)
    convertible(g, body, accent) {
        // carro bem baixinho e curto
        box(g, 1.5, 0.28, 2.4, 0, 0.12, 0.1, body)
        box(g, 1.3, 0.2, 0.7, 0, 0.1, 1.35, body)
        // para-choque cromado
        box(g, 1.55, 0.12, 0.18, 0, 0.05, 1.75, toon(0xcccccc))
        // porta-malas curto
        box(g, 1.35, 0.22, 0.55, 0, 0.18, -1.1, body)
        // assento único pequenino (urso vai estourar)
        box(g, 0.7, 0.25, 0.55, 0, 0.35, -0.05, dark())
        box(g, 0.65, 0.45, 0.12, 0, 0.55, -0.35, dark())
        // volante
        box(g, 0.35, 0.08, 0.08, 0, 0.45, 0.45, toon(0x333333))
        // faróis redondos vintage
        const hl = toon(0xFFFACD, { emissive: 0xFFD700, emissiveIntensity: 0.7 })
        for (const x of [-0.45, 0.45]) {
            const l = new THREE.Mesh(new THREE.CircleGeometry(0.14, 16), hl)
            l.position.set(x, 0.18, 1.72)
            g.add(l)
        }
        // grade frontal com frisos verticais e detalhes cromados
        box(g, 0.7, 0.15, 0.06, 0, 0.12, 1.72, toon(0x111111))
        const chrome = toon(0xd8d8d8, { metalness: 0.82, roughness: 0.2 })
        for (let i = -2; i <= 2; i++) box(g, 0.025, 0.12, 0.025, i * 0.12, 0.12, 1.76, chrome)
        // retrovisores, puxadores e lanternas traseiras deixam a silhueta mais automotiva
        for (const x of [-0.82, 0.82]) {
            box(g, 0.12, 0.06, 0.22, x, 0.28, 0.12, chrome)
            box(g, 0.07, 0.1, 0.12, x * 1.18, 0.4, 0.42, chrome)
            const mirror = new THREE.Mesh(new THREE.SphereGeometry(0.11, 12, 8), chrome)
            mirror.position.set(x * 1.28, 0.48, 0.48); mirror.scale.set(1, 0.72, 0.72); g.add(mirror)
        }
        const tail = toon(0xc91e32, { emissive: 0x7f0010, emissiveIntensity: 0.65 })
        for (const x of [-0.48, 0.48]) box(g, 0.22, 0.12, 0.06, x, 0.2, -1.39, tail)
        box(g, 0.42, 0.13, 0.035, 0, 0.22, -1.4, chrome)
        // escape duplo discreto
        for (const x of [-0.32, 0.32]) {
            const exhaust = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.08, 0.18, 12), chrome)
            exhaust.rotation.x = Math.PI / 2; exhaust.position.set(x, 0.06, -1.55); g.add(exhaust)
        }
    },
    // carro antigo bem cuidado (Gohan — mecânico)
    classic(g, body, accent) {
        // chassi arredondado “anos 60” em blocos
        box(g, 1.85, 0.45, 3.3, 0, 0.22, 0, body)
        // capo longo
        box(g, 1.7, 0.28, 1.1, 0, 0.42, 1.2, body)
        // cabine
        box(g, 1.55, 0.55, 1.2, 0, 0.75, -0.15, body)
        // vidros
        box(g, 1.4, 0.4, 1.0, 0, 0.85, -0.1, toon(0x88ccee, { transparent: true, opacity: 0.55 }))
        // porta-malas
        box(g, 1.65, 0.35, 0.7, 0, 0.45, -1.4, body)
        // cromados
        const chrome = toon(0xdddddd)
        box(g, 1.9, 0.08, 0.15, 0, 0.08, 1.75, chrome)
        box(g, 1.9, 0.08, 0.15, 0, 0.08, -1.75, chrome)
        // linha lateral accent (bem polido)
        box(g, 0.06, 0.08, 2.8, -0.95, 0.35, 0, accent)
        box(g, 0.06, 0.08, 2.8, 0.95, 0.35, 0, accent)
        // faróis redondos
        const hl = toon(0xFFFACD, { emissive: 0xFFD700, emissiveIntensity: 0.65 })
        for (const x of [-0.55, 0.55]) {
            const l = new THREE.Mesh(new THREE.CircleGeometry(0.16, 16), hl)
            l.position.set(x, 0.35, 1.78)
            g.add(l)
        }
        // vidros laterais, borrachas e retrovisores cromados
        const glass = toon(0x83c9e8, { transparent: true, opacity: 0.48, metalness: 0.18, roughness: 0.12 })
        for (const x of [-0.78, 0.78]) {
            box(g, 0.035, 0.29, 0.7, x, 0.82, -0.12, glass)
            box(g, 0.1, 0.045, 0.22, x * 1.12, 0.68, 0.45, toon(0xd5d5d5, { metalness: 0.8, roughness: 0.2 }))
            const mirror = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.13, 0.22), toon(0xbfc8ce, { metalness: 0.72, roughness: 0.22 }))
            mirror.position.set(x * 1.28, 0.78, 0.5); g.add(mirror)
            // maçaneta e friso lateral
            box(g, 0.045, 0.045, 0.18, x * 1.03, 0.42, -0.38, toon(0xe2e2e2, { metalness: 0.85, roughness: 0.2 }))
        }
        // lanternas traseiras, placa e escapamentos metálicos
        const tail = toon(0xd71935, { emissive: 0x7a0010, emissiveIntensity: 0.8 })
        for (const x of [-0.65, 0.65]) box(g, 0.24, 0.12, 0.07, x, 0.42, -1.76, tail)
        box(g, 0.55, 0.15, 0.04, 0, 0.34, -1.78, toon(0xe8e8e8, { metalness: 0.6, roughness: 0.3 }))
        for (const x of [-0.45, 0.45]) {
            const exhaust = new THREE.Mesh(new THREE.CylinderGeometry(0.075, 0.09, 0.2, 12), toon(0x888888, { metalness: 0.8, roughness: 0.22 }))
            exhaust.rotation.x = Math.PI / 2; exhaust.position.set(x, 0.08, -1.83); g.add(exhaust)
        }
        // antena de rádio (charme de mecânico)
        box(g, 0.04, 0.5, 0.04, 0.6, 1.15, -0.8, toon(0x333333))
    },
    // nave espacial neon (Artur — whale de skin)
    neonship(g, body, accent) {
        const neon = new THREE.MeshStandardMaterial({ color: 0x00f0ff, emissive: 0x00dfff, emissiveIntensity: 2.4, metalness: 0.45, roughness: 0.2 })
        const neonPink = new THREE.MeshStandardMaterial({ color: 0xff2d95, emissive: 0xff1688, emissiveIntensity: 2.0, metalness: 0.4, roughness: 0.22 })
        const hull = new THREE.MeshStandardMaterial({ color: body.color, metalness: 0.82, roughness: 0.2, envMapIntensity: 1.5 })
        // casco principal afilado
        box(g, 1.55, 0.42, 3.5, 0, 0.35, 0, hull)
        // swept nose and raised rear deck create a spacecraft silhouette
        box(g, 1.05, 0.28, 1.0, 0, 0.3, 1.8, hull)
        box(g, 0.62, 0.18, 0.8, 0, 0.43, 2.35, neon)
        box(g, 1.35, 0.16, 0.65, 0, 0.52, -1.35, hull)
        // cockpit bolha
        const cock = new THREE.Mesh(
            new THREE.SphereGeometry(0.55, 16, 12, 0, Math.PI * 2, 0, Math.PI * 0.55),
            toon(0x66eeff, { transparent: true, opacity: 0.45, emissive: 0x00aaff, emissiveIntensity: 0.3 })
        )
        cock.position.set(0, 0.75, 0.2)
        cock.castShadow = true
        g.add(cock)
        // asas / aletas laterais
        box(g, 3.0, 0.1, 0.95, 0, 0.25, -0.55, hull)
        box(g, 0.18, 0.56, 0.95, -1.34, 0.46, -0.72, neon)
        box(g, 0.18, 0.56, 0.95, 1.34, 0.46, -0.72, neonPink)
        // swept wingtip fins
        box(g, 0.55, 0.12, 0.45, -1.45, 0.55, -1.15, hull).rotation.y = -0.35
        box(g, 0.55, 0.12, 0.45, 1.45, 0.55, -1.15, hull).rotation.y = 0.35
        // anéis neon no casco
        box(g, 1.65, 0.06, 0.08, 0, 0.55, 0.8, neon)
        box(g, 1.65, 0.06, 0.08, 0, 0.55, -0.4, neonPink)
        box(g, 1.65, 0.06, 0.08, 0, 0.55, -1.2, neon)
        // propulsores traseiros
        const thr = new THREE.MeshStandardMaterial({ color: 0xffb45b, emissive: 0xff4b12, emissiveIntensity: 3.2, metalness: 0.15, roughness: 0.22 })
        const core = new THREE.MeshStandardMaterial({ color: 0xc9fbff, emissive: 0x41eaff, emissiveIntensity: 3.5, roughness: 0.18 })
        for (const x of [-0.48, 0.48]) {
            const c = new THREE.Mesh(new THREE.CylinderGeometry(0.19, 0.27, 0.38, 20), thr)
            c.rotation.x = Math.PI / 2; c.position.set(x, 0.3, -1.85); g.add(c)
            const inner = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.16, 0.4, 16), core)
            inner.rotation.x = Math.PI / 2; inner.position.set(x, 0.3, -2.05); g.add(inner)
            const flame = new THREE.Mesh(new THREE.ConeGeometry(0.18, 0.9, 16), thr)
            flame.rotation.x = -Math.PI / 2; flame.position.set(x, 0.3, -2.55); g.add(flame)
        }
        // underbody neon glow rails on both sides
        box(g, 0.055, 0.055, 2.45, -0.82, 0.08, -0.05, neon)
        box(g, 0.055, 0.055, 2.45, 0.82, 0.08, -0.05, neonPink)
        // painéis de casco e aletas aerodinâmicas menores para dar escala à nave
        const panel = new THREE.MeshStandardMaterial({ color: 0x202a3a, metalness: 0.88, roughness: 0.28 })
        for (const x of [-0.48, 0.48]) {
            box(g, 0.055, 0.035, 1.0, x, 0.58, -0.28, panel)
            box(g, 0.22, 0.08, 0.32, x * 2.5, 0.2, 0.62, hull)
            box(g, 0.035, 0.04, 0.5, x * 1.62, 0.17, -1.15, neon)
        }
        // sensor beacon
        const beacon = new THREE.Mesh(new THREE.SphereGeometry(0.12, 16, 12), core)
        beacon.position.set(0, 1.32, -0.2); g.add(beacon)
        // nariz energy
        box(g, 0.5, 0.2, 0.6, 0, 0.25, 2.35, neon)
        // antena / sensor
        box(g, 0.08, 0.6, 0.08, 0, 1.0, 1.2, neonPink)
    },
}

/** Corpo procedural. skinUrl (opcional) vira textura no corpo (skin). */
export function makeProceduralBody(style, color, skinUrl) {
    const g = new THREE.Group()
    const body = skinUrl ? toon(0xffffff, { map: loadSkinTexture(skinUrl) }) : toon(color)
    const accent = toon(color)
    ;(STYLES[style] || STYLES.kart)(g, body, accent)
    // com skin, a cor do jogador não tinge o corpo (senão suja a textura)
    return { group: g, tint: skinUrl ? [accent] : [body, accent] }
}

export function disposeVisual(obj) {
    obj.traverse((o) => {
        o.geometry?.dispose?.()
        const mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : []
        mats.forEach((m) => m.dispose?.())
    })
}
